/* ════════════════════════════════════════════════════════════════════════ */
/* #region(collapsed) Developer Documentation */
/**
  @license
  # COPYRIGHT NOTICE
    © CROUIN 2021. All rights reserved. Powered by MaxDesign.
    ⚠ FR : CONTENU PROTÉGÉ PAR LES DROITS D'AUTEURS (article L. 111-1 du code de la propriété intellectuelle). Cette ressource ne peut pas être utilisée, diffusée ou exploitée sans l'approbation de ses auteurs. Pour plus d'informations, veuillez contacter admin(at)maxdesign.expert
    ⚠ EN : CONTENT PROTECTED WITH AUTHOR COPYRIGHTS (section L. 111-1 of the intellectual property code). This resource may not be used, distributed or exploited without the approval of its authors. For any information request, please contact admin(at)maxdesign.expert
 
  # INTRODUCTION
    Code powered by MaxDesign.
    This modal uses HTML custom element modal-controller-maxdesign and modal-maxdesign.
    Custom elements extend HTML by creating new elements that carry their own JavaScript for adding new behavior.
    All methods can be accessed with the javascript Class MaxDesign (see "popular javascript methods" below).
 
  # POPULAR JAVASCRIPT METHODS
    const modal = MaxDesign.modal; // get modal object
    const controller = modal.controller; // get modal controller (custom element)
    const cart = modal.cart; // get custom element modal by data-id (cart, localization, ...)
    const modals = controller.modals; // get list of modals in DOM
    modal.cart.open(); // open cart modal
    modal['custom-id'].close(); // close custom modal with data-id 'custom-id'
    document.addEventListener('maxdesign:modal:open', e => console.log(`opening modal: ${e.detail.modal}`)); // run code on modal open event
 
  # MORE INFORMATION
    Contact us at admin(at)maxdesign.expert
*/
/* #endregion ═════════════════════════════════════════════════════════════ */

window.MaxDesign = window.MaxDesign || class {};
MaxDesign.modal = new Proxy(
  {},
  {
    get: (_, prop) => document.querySelector(prop === 'controller' ? '#mcmd' : `.mmd[data-id="${prop}"]`),
  }
);

class ModalController extends HTMLElement {
  connectedCallback() {
    this.aborter = new AbortController();
    this.template = window['modal-template-maxdesign'];
    this.settings = JSON.parse(this.querySelector(':scope > [type="application/json"]').innerText);
    this.modals = [];
    this.triggers = {
      cart: this.settings.find(block => block.cart_triggers)?.cart_triggers,
      editor: this.settings.map(({ heading }) => `a[href="${heading}" i]`).join(),
      custom: '[data-modal-trigger]',
    };
    document.addEventListener('click', this.onDocumentClick.bind(this), { capture: true, signal: this.aborter.signal });
  }

  disconnectedCallback() {
    this.aborter.abort();
  }

  /**
   * Check conditions to trigger modal
   * @param {Object} e - click event
   */
  onDocumentClick(e) {
    this.onModalClose(e);
    this.onModalOpen(e);
  }

  /**
   * Detect close modal event
   * Close when clicking out
   * Close when clicking on flex stretch area (as long as it's not hosting an open modal)
   * Close when clicking on data-close attr
   * Exclude composedPath first item because modal component stretches in mobile
   * We check for [data-modal-persist] in case we interact with an element outside the modal
   * @param {Object} e - target event
   */
  onModalClose(e) {
    const composedPath = e.composedPath();
    [...this.modals]
      .filter(modal => modal.state == 'open')
      .forEach(modal => {
        const clickOut = !composedPath.includes(modal) && composedPath[0] !== document.body;
        const clickStretchedArea = composedPath[0] == modal && !(modal.getAttribute('data-modal') == 'visible');
        const clickClose = composedPath.some(
          node =>
            node instanceof Element &&
            node.hasAttribute('data-close') &&
            (node.getRootNode().host == undefined || node.getRootNode().host == modal)
        );
        if ((clickOut || clickStretchedArea || clickClose) && !e.target.closest('[data-modal-persist]')) {
          e.preventDefault();
          modal.close();
        }
      });
  }

  /**
   * Detect open modal event
   * @param {Object} e - target event
   */
  onModalOpen(e) {
    const target = e.composedPath()[0];
    Object.entries(this.triggers).some(([type, selectors]) => {
      const trigger = target.matches(selectors) ? target : target.closest(selectors);
      if (trigger) {
        e.preventDefault();
        e.stopImmediatePropagation();
        this.getModal(type, trigger);
        return true;
      }
    });
  }

  /**
   * Each modal type has its own set of criterias
   * RequestAnimationFrame + timeout trick https://stackoverflow.com/a/54228987/10294974
   * @param {string} type - editor, cart or custom
   * @param {HTMLObject} trigger - clicked element that is a modal trigger
   */
  async getModal(type, trigger) {
    let modal;
    switch (type) {
      case 'editor':
        const container = trigger.closest('.mmd') || this;
        const frame = container || document.documentElement;
        const obj = this.settings.find(({ heading }) => heading && trigger.href.includes(encodeURI(heading.trim())));
        const modalEl = this.modalQuery(encodeURI(obj?.heading?.trim()));
        modal = modalEl || (await this.buildModal(obj, obj?.heading, frame));
        if (modalEl?.parentElement !== container) container.appendChild(modal);
        requestAnimationFrame(() => setTimeout(() => modal.open(), 25));
        break;

      case 'cart':
        modal = MaxDesign.modal.cart;
        const { UI, controller } = MaxDesign?.cart;
        if (UI) modal.open();
        else if (controller) {
          modal.overlay = { state: 'loading' };
          controller.render(await controller.fetch());
          modal.open();
        } else location.href = trigger.href;
        break;

      default:
        const dataset = trigger.dataset;
        modal = MaxDesign.modal[dataset.modalTrigger];
        if (modal) {
          modal.data = dataset;
          modal.open();
        }
    }
  }

  /**
   * Build editor modal on request
   * @param {Object} obj - block settings of current modal
   * @param {String} id - id of modal (based on heading)
   * @param {Object} frame - either documentElement or a modal parent
   * @returns modal custom element
   */
  async buildModal(obj, id, frame) {
    const modal = document.createElement('modal-maxdesign');
    Object.assign(modal, { overlay: { state: 'loading', frame }, classList: ['mmd'], part: 'modal', slot: 'modal' });
    Object.assign(modal.dataset, { type: 'editor', id });
    modal.style.setProperty('--mmd-height-mobile', `${obj.height_mobile}%`);
    modal.style.setProperty('--mmd-width-desktop', `${obj.modal_width}`);
    modal.innerHTML = `
      <div slot="img" class="mmd__img">${obj.img ? `<img src="${obj.img}&width=100">` : ''}</div>
      <div slot="title" class="mmd__title">${obj.title}</div>
      <div slot="content" class="mmd__content">${
        obj.liquid ? obj.liquid : obj.url ? await this.fetchPage(obj.url) : obj.richtext
      }</div>`;
    return modal;
  }

  /**
   * Fetch a page asynchronously
   * @param {String} handle - page url handle
   * @returns page body
   */
  async fetchPage(handle) {
    try {
      const response = await fetch(handle + '.json');
      const data = await response.json();
      const json = Object.values(data)[0];
      const html = json.body_html || json.body || json.description;
      return html?.replace(/<img\s+src=/g, '<img loading="lazy" src=');
    } catch (message) {
      console.error(message);
      location.href = handle;
    }
  }

  /**
   * Find existing modal based on its data-id
   */
  get modalQuery() {
    return modalId => this.modals.find(modal => modal.dataset.id == modalId);
  }
}

class Modal extends HTMLElement {
  connectedCallback() {
    this.aborter = new AbortController();
    this.controller = MaxDesign.modal.controller;
    this.controller.modals.push(this);
    this.root = this.shadow ? this.attachShadow({ mode: 'open' }) : this;
    this.shadow && this.root.appendChild(this.controller.template.content.cloneNode(true));
    this.settings = this.json(this);
    this.container = this.root.querySelector('[part="container"]') || this.root;
    this._frame = this.frame;
    this.onResize();
  }

  disconnectedCallback() {
    this.aborter.abort();
    this.controller.modals.splice(this.controller.modals.indexOf(this), 1);
    this.state == 'open' && this.close(); /* useful for cart nested modals */
  }

  onResize() {
    addEventListener('resize', this.throttle(this.roundDimensions.bind(this)), { signal: this.aborter.signal });
    new ResizeObserver(this.throttle(this.roundDimensions.bind(this))).observe(this.container);
  }

  async open() {
    if (this.state != 'open') {
      await this.loadResources();
      this.overlay = { state: 'visible' };
      this.setAttribute('part', 'modal open');
      this.event = 'open';
    } else this.overlay = { state: 'visible' };
  }

  close() {
    this.overlay = { state: 'hidden' };
    this.setAttribute('part', 'modal');
    this.event = 'close';
  }

  /**
   * Load modal resources asynchronously if there are any
   * Script and stylesheet can be added in script/json { "resources": { "script": "...", "stylesheet": "..." } }
   */
  async loadResources() {
    const resources = this.settings?.resources;
    if (!resources || this.resourcesLoaded) return;
    this.overlay = { state: 'loading' };
    const promises = [];
    if (resources.stylesheet) {
      promises.push(
        new Promise(resolve => {
          this.appendChild(
            Object.assign(document.createElement('link'), {
              rel: 'stylesheet',
              href: resources.stylesheet,
              type: 'text/css',
              onload: () => ((this.event = 'stylesheet:ready'), resolve()),
            })
          );
        })
      );
    }
    if (resources.script) {
      promises.push(
        new Promise(resolve => {
          this.appendChild(
            Object.assign(document.createElement('script'), {
              src: resources.script,
              onload: () => ((this.event = 'script:ready'), resolve()),
            })
          );
        })
      );
    }
    await Promise.all(promises);
    this.roundDimensions();
    this.resourcesLoaded = true;
  }

  /**
   * Round modal dimensions to prevent blured content in webkit
   */
  roundDimensions() {
    if (this.classList.contains('mmd--right')) return;
    this.style.removeProperty('width');
    this.style.removeProperty('height');
    this.style.width = this.dimensions(this.offsetWidth, innerWidth * 0.85);
    this.style.height = this.dimensions(this.offsetHeight, innerHeight * 0.85);
  }

  throttle(callback, delay = 200) {
    let lastCall = 0;
    let timeout;
    return (...args) => {
      const now = Date.now();
      if (now - lastCall >= delay) {
        callback(...args);
        lastCall = now;
      } else {
        clearTimeout(timeout);
        timeout = setTimeout(() => {
          callback(...args);
          lastCall = now;
        }, delay);
      }
    };
  }

  /**
   * Set main overlay
   * @param {string} state - loading, visible or hidden
   * @param {Object} host - documentElement or a modal parent
   */
  set overlay({ state, frame = this.frame }) {
    frame.dataset.modal = state;
  }

  /**
   * Dispatch custom event
   * @param {Boolean} state - open or close
   */
  set event(state) {
    document.dispatchEvent(new CustomEvent(`maxdesign:modal:${state}`, { detail: this }));
  }

  set scroll({ xPos = 0, yPos = 0 }) {
    this.container.scrollTo({ top: xPos, left: yPos, behavior: 'smooth' });
  }

  set data({ id, modalTrigger, modalTitle, modalContent, ...data }) {
    this.details = { ...data };
    if (modalTitle) this.title = modalTitle;
    if (modalContent) this.content = modalContent;
    Object.assign(this.dataset, data);
  }

  set title(title) {
    this.querySelector('[slot="title"]').innerHTML = title;
  }

  set content(content) {
    this.querySelector('[slot="content"]').innerHTML = content;
  }

  /**
   * Get overlay frame
   * If modal is nested, return parent Modal
   */
  get frame() {
    return this.isConnected ? this.parentElement.closest('.mmd') || document.documentElement : this._frame;
  }

  get state() {
    return this.getAttribute('part')?.includes('open') ? 'open' : 'close';
  }

  get shadow() {
    return (this._shadow = this._shadow || !this.hasAttribute('data-light-dom'));
  }

  get dimensions() {
    return (number, maxNumber) =>
      Math.abs(maxNumber - number) < 1 ? `${Math.floor(maxNumber / 2) * 2}px` : `${Math.ceil(number / 2) * 2}px`;
  }

  get json() {
    return scope => JSON.parse(scope.querySelector(':scope > [type="application/json"]')?.innerText || '{}');
  }
}

customElements.define('modal-controller-maxdesign', ModalController);
customElements.define('modal-maxdesign', Modal);
